import { DEFAULT_ACCESS_TOKEN_KEY } from 'src/shared/graphql';
import { logError } from 'src/shared/telemetry/logger';

/**
 * Requests access to non-partitioned local storage, and retrieves the access token if it exists.
 * Iframes in most modern browsers have partitioned local storage by default, so you cannot just access the parent window's local storage.
 *
 * Note that only Chrome currently supports this API.
 * https://developer.mozilla.org/en-US/docs/Web/API/StorageAccessHandle/localStorage
 *
 * @returns {Promise<RequestTokenFromPartitionedLocalStorageResponse>} A promise that resolves to an object containing the access token and whether local storage was accessed.
 * @property {string | null} accessToken - The access token stored in local storage.
 * @property {boolean} localStorageAccessed - If local storage was accessed. This will be false if the browser does not support the API.
 */

export interface RequestTokenFromPartitionedLocalStorageResponse {
  accessToken: string | null;
  localStorageAccessed: boolean;
}

// @ts-ignore - This is a workaround for the fact that the `requestStorageAccess` API is not yet in the TypeScript DOM types.
export interface DocumentWithStorageAccess extends Document {
  requestStorageAccess?: (options: {
    localStorage: boolean;
  }) => Promise<StorageAccessHandle | undefined>;
}

interface StorageAccessHandle {
  localStorage: Storage;
}

export const requestTokenFromPartitionedLocalStorage =
  async (): Promise<RequestTokenFromPartitionedLocalStorageResponse> => {
    const response: RequestTokenFromPartitionedLocalStorageResponse = {
      accessToken: null,
      localStorageAccessed: false,
    };

    const doc = document as DocumentWithStorageAccess;

    // Generally, it seems like browsers will have `requestStorageAccess` but only Chrome returns a handle.
    if (doc.requestStorageAccess) {
      try {
        const handle = await doc.requestStorageAccess({ localStorage: true });

        if (handle) {
          response.accessToken = handle.localStorage.getItem(
            DEFAULT_ACCESS_TOKEN_KEY,
          );
          response.localStorageAccessed = true;
        }
      } catch (error) {
        // Logging this for now until we figure out what the expected error behavior for this action would be.
        logError(error as Error);
      }
    }

    return response;
  };
